export const sleep = (ms = 0) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Comparador seguro para ordenar columnas de tablas. Evita el error
 * "x.localeCompare is not a function" cuando el valor no es string (number, null,
 * undefined u objeto). Reglas:
 *  - null/undefined/'' van al final (en orden ascendente).
 *  - números se comparan numéricamente.
 *  - el resto se compara como string con locale 'es' y `numeric` (para que
 *    "10" quede después de "2", no antes).
 * Devuelve el resultado en orden ascendente; el `sortingOrder` de la tabla lo invierte.
 */
export function safeCompare(a: unknown, b: unknown): number {
  const aEmpty = a === null || a === undefined || a === ''
  const bEmpty = b === null || b === undefined || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? 1 : -1
  return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' })
}

/** Validation */
export const validators = {
  numero: (v: string) => {
    const pattern = /^\d+$/
    return pattern.test(v) || 'Por favor ingresa un número válido'
  },
  numeroFloat: (v: string) => {
    const pattern = /^\d+(.\d+)?$/
    return pattern.test(v) || 'Por favor ingresa un número válido (puede incluir decimales)'
  },
  minValue: (min: number) => {
    return (v: number) => v >= min || `El valor debe ser al menos ${min}`
  },
  maxValue: (max: number) => {
    return (v: number) => v <= max || `El valor debe ser como máximo ${max}`
  },
  email: (v: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return pattern.test(v) || 'Por favor ingresa un correo electrónico válido'
  },
  nonfemail: (v: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!v) return true // Allow empty/null values
    return pattern.test(v) || 'Por favor ingresa un correo electrónico válido'
  },
  telefono: (v: string) => {
    if (!v) return true
    const clean = String(v).replace(/\D/g, '')
    const isValid = clean.length === 8 || clean.length === 9 || clean.length === 11 || clean.length === 12
    return isValid || 'Por favor ingresa un número de teléfono válido'
  },
  required: (v: any) => !!v || 'Este campo es obligatorio',
  patente: (v: string) => {
    const pattern1 = /^[A-Z]{2}\d{4}$/
    const pattern2 = /^[A-Z]{4}\d{2}$/
    return pattern1.test(v) || pattern2.test(v) || 'Por favor ingresa una patente válida (Formato: AA1234 o AABB12)'
  },
  conditionalValidator: (condition: boolean, validator: (v: any) => boolean | string) => {
    console.log('Calling conditional validator', condition)
    return (v: any) => {
      if (condition) {
        return validator(v)
      }
      return true
    }
  },
  notRepeated: (
    list: string[],
    equalizer: (v: string) => string = (v) => v,
    message: string = 'El valor ya existe',
  ) => {
    return (v: string) => {
      const includes = list.findIndex((item) => equalizer(item) === equalizer(v)) >= 0
      return !includes || message
    }
  },
  notRepeatedAdded: (
    added: string,
    adder: (a: string, b: string) => string,
    list: string[],
    equalizer: (v: string) => string = (v) => v,
    message: string = 'El valor ya existe',
  ) => {
    return (v: string) => {
      const includes = list.findIndex((item) => equalizer(item) === equalizer(adder(added, v))) >= 0
      return !includes || message
    }
  },
  ruledPassword: (rules: ((v: string) => boolean | string)[]) => {
    return (v: string) => {
      for (const rule of rules) {
        const result = rule(v)
        if (result !== true) {
          return result
        }
      }
      return true
    }
  },
  sameClave: (original: string) => {
    return (v: string) => v === original || 'Las contraseñas no coinciden'
  },
  rut(v: string) {
    const pattern1 = /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/
    const pattern2 = /^\d{7,8}-[\dkK]$/
    return pattern1.test(v) || pattern2.test(v) || 'Por favor ingresa un RUT válido (Formato: XX.XXX.XXX-X)'
  },
  pasaporte(v: string) {
    const pattern = /^[A-Za-z]{3}\d{6}$/
    return pattern.test(v) || 'Por favor ingresa un pasaporte válido (Formato: AAA123456)'
  },
  rutOPasaporte(v: string) {
    const pattern1 = /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/
    const pattern2 = /^\d{7,8}-[\dkK]$/
    const pattern3 = /^[A-Za-z]{3}\d{6}$/
    return pattern1.test(v) || pattern2.test(v) || pattern3.test(v) || 'Por favor ingresa un RUT o pasaporte válido'
  },
  minLengthString: (min: number) => {
    return (v: string) => (v && v.length >= min) || `El valor debe tener al menos ${min} caracteres`
  },
  minLengthArray: (min: number) => {
    return (v: any[]) => (v && v.length >= min) || `El valor debe tener al menos ${min} elementos`
  },
  prevDate: (date: Date) => {
    return (v: Date) => (v && v <= date) || `La fecha debe ser anterior o igual a ${date.toLocaleDateString()}`
  },
  nextDate: (date: Date) => {
    return (v: Date) => (v && v >= date) || `La fecha debe ser posterior o igual a ${date.toLocaleDateString()}`
  },
}

export const passwordRules: ((v: string) => boolean | string)[] = [
  (v) => (v && v.length >= 8) || 'La contraseña debe tener al menos 8 caracteres',
  (v) => (v && /[A-Za-z]/.test(v)) || 'La contraseña debe contener al menos una letra',
  (v) => (v && /\d/.test(v)) || 'La contraseña debe contener al menos un número',
  (v) => (v && /[!@#$%^&*(),.?":{}|<>]/.test(v)) || 'La contraseña debe contener al menos un carácter especial',
]
